import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import {
  Card,
  Table,
  Input,
  Button,
  Select,
  Typography,
  Tag,
} from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import promoService from "../../services/promoService";
import { useCommon } from '../../context/CommonContext';
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const PromoList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle("Promos");
  }, [setPageTitle]);
  // State management
  const [loading, setLoading] = useState(false);
  const [promos, setPromos] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} promos`,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });

  const getPromos = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          ...filters,
          ...params,
        };

        console.log("Fetching promos with params:", requestParams);
        const response = await promoService.getAllPromos(requestParams);
        console.log("Promos API Response:", response);

        if (response.data && response.data.status) {
          const promosData = response.data.data || [];

          console.log("Promos data:", promosData);

          // Ensure promosData is an array
          if (Array.isArray(promosData)) {
            setPromos(promosData);
            setPagination((prev) => ({
              ...prev,
              total: promosData.length,
            }));
          } else {
            console.error("Promos data is not an array:", promosData);
            setPromos([]);
          }
        } else {
          notify.error({ message: "Failed to fetch promos" });
          setPromos([]);
        }
      } catch (error) {
        console.error("Error fetching promos:", error);
        notify.error({ message: "Failed to fetch promos" });
        setPromos([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, notify]
  );

  // Load promos data
  useEffect(() => {
    getPromos();
  }, [getPromos]);

  // Handle search (explicit, e.g. enter / button click)
  const handleSearch = (value) => {
    const trimmed = (value || "").trim();
    setFilters((prev) => ({ ...prev, search: trimmed }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPromos({
      search: trimmed,
      status: filters.status,
    });
  };

  // Debounced search-as-you-type
  const searchDebounceRef = useRef(null);
  const handleSearchInput = (e) => {
    const value = e.target.value;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPromos({
      search: filters.search,
      status: value,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));
  };

  // Handle add promo
  const handleAddPromo = () => {
    navigate("/promo/add/new");
  };

  // Handle edit promo
  const handleEditPromo = (promoId) => {
    navigate(`/promo/edit/${promoId}`);
  };

  // Format date
  const getDateString = (val) => {
    if (val) {
      const dateStr = new Date(val).toLocaleDateString();
      return dateStr;
    } else {
      return "No Date";
    }
  };

  // Table columns
  const columns = [
    {
      title: "Name",
      dataIndex: "promo_name",
      key: "promo_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.promo_name.localeCompare(b.promo_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Promo Code",
      dataIndex: "promo_code",
      key: "promo_code",
      render: (text) => (
        <Tag color="blue" style={{ fontSize: "12px" }}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Redeems",
      dataIndex: "no_of_redeem_used",
      key: "no_of_redeem_used",
      width: 100,
      sorter: (a, b) => (a.no_of_redeem_used || 0) - (b.no_of_redeem_used || 0),
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || 0}</Text>,
    },
    {
      title: "Start Date",
      dataIndex: "start",
      key: "start",
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: "13px" }}>{getDateString(text)}</Text>
      ),
    },
    {
      title: "Expiration",
      dataIndex: "expiration",
      key: "expiration",
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: "13px" }}>{getDateString(text)}</Text>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (text) => (
        <Text style={{ fontSize: "13px" }}>{text ? new Date(text).toLocaleDateString() : "—"}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPromo(record.promo_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Promos ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 360,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="All"
            style={{ width: 120, flexShrink: 0 }}
            allowClear
          >
            <Option value="">All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
            <Option value="EXPIRED">Expired</Option>
          </Select>
          <Search
            placeholder="Search by name, code, or description…"
            allowClear
            onSearch={handleSearch}
            onChange={handleSearchInput}
            style={{ flex: "1 1 160px", maxWidth: 320 }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPromo}
            size="middle"
          >
            Add Promo
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={promos}
        rowKey="promo_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        size="middle"
        locale={{
          emptyText: "No promos found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default PromoList;
